// ===========================================
// COMPONENTE: ImpuestoBadge
// ===========================================
// Badge coloreado para mostrar un impuesto

import { cn } from '@/lib/utils'
import { COLORES_IMPUESTO, type ColorImpuesto } from '@/types/impuestos'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

interface ImpuestoBadgeProps {
  nombre: string          // Nombre corto a mostrar
  color?: ColorImpuesto   // Color del badge
  className?: string
  size?: 'sm' | 'md'
}

export function ImpuestoBadge({
  nombre,
  color = 'gray',
  className,
  size = 'sm',
}: ImpuestoBadgeProps) {
  const colores = COLORES_IMPUESTO[color] || COLORES_IMPUESTO.gray

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md border font-medium',
        colores.bg,
        colores.text,
        colores.border,
        size === 'sm' ? 'px-1.5 py-0.5 text-xs' : 'px-2 py-1 text-sm',
        className
      )}
    >
      {nombre}
    </span>
  )
}

// Lista de badges
interface ImpuestosBadgeListProps {
  impuestos: Array<{
    nombre_corto: string
    color: ColorImpuesto
  }>
  max?: number           // Maximo a mostrar antes de "+N"
  className?: string
  /** Modo compacto: solo muestra el primero + contador cuando hay poco espacio */
  compact?: boolean
}

export function ImpuestosBadgeList({
  impuestos,
  max = 3,
  className,
  compact = false,
}: ImpuestosBadgeListProps) {
  if (!impuestos.length) {
    return <span className="text-muted-foreground text-sm">-</span>
  }

  // En modo compacto, mostrar solo 1 + contador
  const effectiveMax = compact ? 1 : max
  const visibles = impuestos.slice(0, effectiveMax)
  const ocultos = impuestos.length - effectiveMax

  return (
    <div className={cn('flex gap-1 items-center', className)}>
      {visibles.map((imp, i) => (
        <ImpuestoBadge
          key={i}
          nombre={imp.nombre_corto}
          color={imp.color}
          className="shrink-0"
        />
      ))}
      {ocultos > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center px-1.5 py-0.5 text-xs text-muted-foreground shrink-0 cursor-pointer hover:text-foreground hover:bg-muted rounded transition-colors"
              aria-label="Ver todos los impuestos"
              title="Ver todos los impuestos"
            >
              +{ocultos}
            </button>
          </PopoverTrigger>
          <PopoverContent side="top" align="start" className="w-auto p-2">
            <div className="flex flex-wrap gap-1">
              {impuestos.map((imp, i) => (
                <ImpuestoBadge
                  key={i}
                  nombre={imp.nombre_corto}
                  color={imp.color}
                />
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}
